import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
// API service removed - using PHP backend directly
import SEO from '../components/SEO';
import blogApiService from '../services/blogApiService';

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPost();
  }, [id]);

  const loadPost = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch post by slug or ID from external dashboard API
      const postData = await blogApiService.getPost(id);
      
      if (postData) {
        let formattedDate = 'Recently published';
        try {
          const d = new Date(postData.published_at || postData.created_at);
          if (!isNaN(d.getTime())) {
            formattedDate = d.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          }
        } catch {
          // fallback
        }

        setPost({
          id: postData.id,
          title: postData.title,
          excerpt: postData.excerpt,
          content: postData.content,
          author_name: postData.author_name || postData.author || 'DevInquire Team',
          created_at: formattedDate,
          updated_at: postData.updated_at,
          category: postData.category_name || postData.category || 'Uncategorized',
          featured_image: postData.featured_image || 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1000&q=80',
          tags: Array.isArray(postData.tags) ? postData.tags : [],
          readTime: postData.read_time ? `${postData.read_time} min read` : '5 min read',
          slug: postData.slug || postData.id
        });
      } else {
        setError('Post not found');
      }
    } catch (error) {
      console.error('Error loading post:', error);
      setError(error.message || 'Failed to load post. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">📝</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {error ? 'Error Loading Post' : 'Post Not Found'}
          </h1>
          <p className="text-gray-600 mb-6">
            {error || 'The blog post you\'re looking for doesn\'t exist or has been removed.'}
          </p>
          <div className="space-y-3">
            {error && (
              <button
                onClick={() => loadPost()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 mr-3"
              >
                Try Again
              </button>
            )}
            <Link
              to="/blog"
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`${post.title} - DevInquire Blog`}
        description={post.excerpt || `Read ${post.title} on DevInquire blog. Expert insights on web development, mobile apps, and digital innovation.`}
        keywords={`${post.category}, ${post.title}, web development, programming, tech blog, ${Array.isArray(post.tags) ? post.tags.join(', ') : ''}`}
        canonical={`https://devinquire.com/blog/${post.slug || post.id}`}
        ogTitle={post.title}
        ogDescription={post.excerpt || `Read ${post.title} on DevInquire blog.`}
        ogUrl={`https://devinquire.com/blog/${post.slug || post.id}`}
        ogImage={post.featured_image}
        schemaType="BlogPosting"
        author={post.author_name || 'DevInquire Team'}
        datePublished={post.created_at}
        dateModified={post.updated_at || post.created_at}
        articleSection={post.category}
      />
      <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-[#4e45e1] text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-4 mb-6">
            <Link
              to="/blog"
              className="text-blue-200 hover:text-white transition-colors duration-200 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Blog</span>
            </Link>
          </div>
          
          <div className="flex items-center justify-center space-x-4 mb-6">
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
              {post.category}
            </span>
            <span className="text-blue-200">•</span>
            <span className="text-blue-200">{post.readTime || '5 min read'}</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>
          
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
            {post.excerpt}
          </p>
          
          <div className="flex items-center justify-center space-x-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white font-bold">
              {(post.author_name || 'Admin User').split(' ').map(n => n[0]).join('')}
            </div>
            <div className="text-left">
              <div className="font-medium text-white">{post.author_name || 'Admin User'}</div>
              <div className="text-blue-200 text-sm">
                {post.created_at}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <section className="py-8 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <img
              src={post.featured_image}
              alt={`Featured image for article: ${post.title}`}
              className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
              {post.content}
            </div>
          </div>
          
          {/* Tags */}
          {(post.tags || []).length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {(post.tags || []).map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full hover:bg-blue-200 transition-colors duration-200"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Author Info */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-[#4e45e1] rounded-full flex items-center justify-center text-white font-bold text-xl">
                {(post.author_name || 'Admin User').split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{post.author_name || 'Admin User'}</h3>
                <p className="text-gray-600">Author at DevInquire</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">More Posts</h2>
          <div className="text-center">
            <Link
              to="/blog"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200 inline-flex items-center space-x-2"
            >
              <span>View All Posts</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}